import { useState, useCallback, useRef, useEffect } from 'react';
import { sb } from '../supabase';
import { saveDraftFiles, loadDraftFiles, clearDraftFiles } from '../lib/draftFiles';
import { ATTACH_MAX_BYTES, ATTACH_MAX_COUNT } from '../lib/requestFormOptions';

// One hook for buyer request attachments: hold the picked files, then upload
// them to the PRIVATE request-attachments bucket at submit time.
//
// Files are held in memory until uploadAll() is called, because a buyer can
// compose a request before signing in — nothing reaches storage until the
// request is actually sent and a user id exists.
//
// ── uploadAll() DOES NOT THROW ──────────────────────────────────────────────
// It returns { paths, failed }. That is the difference from useAttachmentPicker,
// whose uploadAll throws and whose only caller treats any failure as fatal and
// abandons the request. The decision is the opposite: a failed upload publishes
// the request WITH A WARNING and never blocks it. A buyer who has typed a full
// specification should not lose it because one PDF did not reach a bucket.
//
// The caller therefore has to decide what to do with `failed` — which is the
// point. Returning a partial result forces that choice to be visible, where a
// throw let it default to "abandon everything".
//
// ── Bucket and path ─────────────────────────────────────────────────────────
// PRIVATE bucket `request-attachments` only. Nothing here writes to the public
// product-images bucket.
//
// Path: {userId}/{sessionId}/{uniq}_{safeName}
//
// The first segment MUST be the uploader's uid: the storage policy is
//   auth.uid()::text = (storage.foldername(name))[1]
// (migration 20260801000004), so any other first segment is rejected by RLS.
//
// That migration's header describes the convention as
// {buyer_uid}/{request_id}/{filename}, and production paths do NOT look like
// that — there is no request_id at upload time, because the upload happens
// before the request row is inserted. The uid/session/file form below is what
// is actually stored, and changing it would orphan every existing attachment.
//
// factory-attachment-url imposes NO format of its own: it checks exact set
// membership against request_customization.attachment_paths before signing, so
// what matters is that the same string written here is the string stored there.

let _seq = 0;
const uniq = () => `${Date.now().toString(36)}${(_seq++).toString(36)}`;
const safeName = (name) => String(name || 'file').replace(/[^\w.-]+/g, '_').slice(-80);

/** The private bucket. Exported so callers and tests name it once. */
export const ATTACHMENT_BUCKET = 'request-attachments';

/**
 * Why a failure is a failure. Stable codes — the UI maps these to copy, and a
 * caller may want to treat them differently (no_user is recoverable by signing
 * in; upload_failed is not recoverable by the buyer).
 */
export const FAIL_NO_USER = 'no_user';
export const FAIL_UPLOAD = 'upload_failed';

const MSG = {
  ar: {
    count: `الحد الأقصى ${ATTACH_MAX_COUNT} ملفات.`,
    big: (n) => `«${n}» أكبر من 10 ميجابايت.`,
    failed: (n) => `تعذّر رفع «${n}».`,
    noUser: (n) => `تعذّر رفع «${n}» — يلزم تسجيل الدخول.`,
  },
  en: {
    count: `Up to ${ATTACH_MAX_COUNT} files.`,
    big: (n) => `"${n}" is larger than 10 MB.`,
    failed: (n) => `"${n}" could not be uploaded.`,
    noUser: (n) => `"${n}" could not be uploaded — sign-in required.`,
  },
  zh: {
    count: `最多 ${ATTACH_MAX_COUNT} 个文件。`,
    big: (n) => `“${n}” 超过 10 MB。`,
    failed: (n) => `“${n}” 上传失败。`,
    noUser: (n) => `“${n}” 上传失败 — 需要先登录。`,
  },
};

// ── persistKey (owner, 2026-10-17) ─────────────────────────────────────────
// A form whose draft survives the sign-in redirect passes a key, and its files
// survive it too: restored from IndexedDB when the form opens, saved on every
// change, removed by reset() / clearPersisted() (call it once the request is
// sent). Without a key nothing is stored — the hook behaves as before.
// `hydrated` turns true once the restore attempt has finished (immediately
// when there is no key); `restoredCount` says how many files came back.
export default function useRequestAttachments(lang = 'ar', { persistKey = null } = {}) {
  const m = MSG[lang] || MSG.ar;
  const [files, setFiles] = useState([]);   // [{ id, file, name, size }]
  const [error, setError] = useState('');   // picker-time validation only
  const sessionRef = useRef(uniq());        // one folder per compose session
  const [hydrated, setHydrated] = useState(!persistKey);
  const [restoredCount, setRestoredCount] = useState(0);

  // Restore once per key. Saving waits for this, so the empty first render
  // cannot overwrite what is stored before it has been read.
  useEffect(() => {
    if (!persistKey) { setHydrated(true); return undefined; }
    let alive = true;
    setHydrated(false);
    loadDraftFiles(persistKey).then((stored) => {
      if (!alive) return;
      const back = (stored || []).slice(0, ATTACH_MAX_COUNT)
        .map((f) => ({ id: uniq(), file: f, name: f.name || 'file', size: f.size || 0 }));
      if (back.length) setFiles((prev) => (prev.length ? prev : back));
      setRestoredCount(back.length);
      setHydrated(true);
    });
    return () => { alive = false; };
  }, [persistKey]);

  useEffect(() => {
    if (!persistKey || !hydrated) return;
    saveDraftFiles(persistKey, files.map((f) => f.file));
  }, [files, hydrated, persistKey]);

  const clearPersisted = useCallback(() => (persistKey ? clearDraftFiles(persistKey) : Promise.resolve(false)), [persistKey]);

  /**
   * Validation happens HERE, in the callback, not inside the setState updater.
   * useAttachmentPicker calls setError from within its updater; React may run
   * an updater more than once, and a side effect in one is a bug waiting for
   * StrictMode to find it.
   */
  const addFiles = useCallback((fileList) => {
    const incoming = Array.from(fileList || []);
    if (incoming.length === 0) return;

    const accepted = [];
    let nextError = '';
    let count = files.length;

    for (const f of incoming) {
      if (count >= ATTACH_MAX_COUNT) { nextError = m.count; break; }
      if (f.size > ATTACH_MAX_BYTES) { nextError = m.big(f.name); continue; }
      accepted.push({ id: uniq(), file: f, name: f.name, size: f.size });
      count += 1;
    }

    if (accepted.length) setFiles((prev) => [...prev, ...accepted]);
    setError(nextError);
  }, [files, m]);

  const removeFile = useCallback((id) => {
    setFiles((prev) => prev.filter((x) => x.id !== id));
    setError('');
  }, []);

  const reset = useCallback(() => {
    setFiles([]);
    setError('');
    sessionRef.current = uniq();
    if (persistKey) clearDraftFiles(persistKey);
  }, [persistKey]);

  /**
   * Upload every held file. NEVER throws — see the note at the top.
   *
   * Returns { paths, failed }:
   *   paths  string[]  object paths that uploaded, in pick order
   *   failed [{ id, name, reason, message }]  one entry per file that did not
   *
   * Both can be non-empty at once; that is the case worth designing for, since
   * it is the one a caller is most likely to render wrongly.
   *
   * Files upload one at a time rather than in parallel. The bucket is private
   * and per-buyer, there are at most six, and serial keeps `paths` in pick
   * order — which is the order the factory sees them in.
   */
  const uploadAll = useCallback(async (userId) => {
    const paths = [];
    const failed = [];

    if (files.length === 0) return { paths, failed };

    // No session means RLS would reject every object anyway: the policy pins
    // the first path segment to auth.uid(). Failing here names the reason
    // instead of returning six identical storage errors.
    if (!userId) {
      return {
        paths,
        failed: files.map((f) => ({
          id: f.id, name: f.name, reason: FAIL_NO_USER, message: m.noUser(f.name),
        })),
      };
    }

    const session = sessionRef.current;

    for (const item of files) {
      const path = `${userId}/${session}/${uniq()}_${safeName(item.name)}`;
      try {
        const { error: upErr } = await sb.storage.from(ATTACHMENT_BUCKET)
          .upload(path, item.file, {
            upsert: true,
            contentType: item.file?.type || undefined,
          });
        if (upErr) {
          // The provider's own message, in the log. A bucket rejects on mime
          // type and size as well as on auth, and "upload failed" alone does
          // not tell anyone which.
          console.error('[useRequestAttachments] upload rejected:', item.name, upErr.message || upErr);
          failed.push({ id: item.id, name: item.name, reason: FAIL_UPLOAD, message: m.failed(item.name) });
          continue;
        }
        paths.push(path);
      } catch (e) {
        // A throwing client — network stack, aborted request — must not escape
        // and take the whole request submission with it.
        console.error('[useRequestAttachments] upload threw:', item.name, e?.message || e);
        failed.push({ id: item.id, name: item.name, reason: FAIL_UPLOAD, message: m.failed(item.name) });
      }
    }

    return { paths, failed };
  }, [files, m]);

  return { files, error, addFiles, removeFile, reset, uploadAll, hydrated, restoredCount, clearPersisted };
}
